'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Surface } from '#pplx/frontend/aether/components/Surface/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { MainSurfaceContext } from '#pplx/frontend/apps/ask/layout/page/AppLayoutV2MainSurfaceContext.js';
import { ScrollContainerRefProvider } from '#pplx/frontend/apps/ask/layout/page/ScrollContainerRefProvider.js';
import { ScrollContainerRefContext } from '#pplx/frontend/apps/ask/layout/page/ScrollContainerRefProviderShared.js';
import { AppLayoutProvider } from '#pplx/frontend/apps/ask/layout/primitives/AppLayoutProvider.js';
import { useChromeVisibility } from '#pplx/frontend/apps/ask/layout/primitives/ChromeVisibilityProviderShared.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { animated } from '@react-spring/web';
import classNames from 'classnames';
import { useContext, useRef, useState } from 'react';
import { match } from 'ts-pattern';
const MobileSidebar = Dynamic(async () => {
    const { MobileSidebar } = await import('#pplx/frontend/apps/ask/layout/page/MobileSidebar.js');
    return { default: MobileSidebar };
});
const MobileProductHeader = Dynamic(async () => {
    const { MobileProductHeader } = await import('#pplx/frontend/apps/ask/layout/header/MobileProductHeader.js');
    return { default: MobileProductHeader };
});
const Wrapper = ({ children, ref, }) => {
    const scrollContainerRef = useRef(null);
    return (_jsx(AppLayoutProvider, { children: _jsx(ScrollContainerRefProvider, { scrollContainerRef: scrollContainerRef, children: _jsx(BoxPrimitive, { bg: "background", children: _jsx("div", { className: "isolate flex h-dvh", ref: ref, children: children }) }) }) }));
};
const Sidebar = ({ children }) => {
    const { isMobileStyle } = useIsMobile({ evaluateOnInit: true });
    return isMobileStyle ? _jsx(MobileSidebar, { children: children }) : _jsx(_Fragment, { children: children });
};
const ContentWrapper = ({ children, fillHeight = true, ref, banner, }) => {
    const [contentSurfaceElement, setContentSurfaceElement] = useState();
    return (_jsxs("main", { className: "erp-tab:p-0 erp-tab:gap-0 isolate flex h-auto max-h-screen min-w-0 grow flex-col", ref: ref, children: [banner, _jsxs(BoxPrimitive, { bg: "background", className: "@container/main relative isolate min-h-0 flex-1 overflow-clip bg-clip-border", ref: setContentSurfaceElement, children: [_jsx(MainSurfaceContext.Provider, { value: contentSurfaceElement, children: _jsx("div", { className: classNames(`mx-auto flex w-full flex-col`, {
                                'h-full': fillHeight,
                            }), children: children }) }), _jsx(BoxPrimitive, { className: "rounded-inherit pointer-events-none absolute inset-0 z-20 hidden md:dark:block md:!border-y-0 md:!border-r-0" })] })] }));
};
const Content = ({ children, className, childrenWidth, containerClassName = '', scrolling = 'auto', shouldUseSurface = true, }) => {
    const width = match(childrenWidth)
        .with('small', () => 'max-w-screen-sm px-4 md:px-8')
        .with('default', () => 'max-w-screen-md px-4 md:px-8')
        .with('static', () => 'max-w-screen-lg px-4 py-8 md:px-12')
        .with('large', () => 'max-w-threadWidth px-4 md:px-8')
        .with('none', () => 'max-w-none')
        .otherwise(() => 'max-w-screen-md px-4 md:px-8');
    const scrollContainerContext = useContext(ScrollContainerRefContext);
    const scrollContainerRef = scrollContainerContext?.scrollContainerRef;
    const scrollContainerClassName = classNames('scrollable-container flex flex-1 basis-0', {
        'overflow-auto [scrollbar-gutter:stable]': scrolling === 'auto',
        'overflow-hidden [scrollbar-gutter:auto]': scrolling === 'none',
    }, className);
    const content = (_jsx("div", { className: classNames('mx-auto size-full', width, containerClassName), children: children }));
    if (!shouldUseSurface) {
        return (_jsx("div", { ref: scrollContainerRef, className: scrollContainerClassName, children: content }));
    }
    return (_jsx(Surface, { ref: scrollContainerRef, className: scrollContainerClassName, children: content }));
};
const ContentHeader = ({ children, mobileHeader = true, }) => {
    const { isMobileStyle } = useIsMobile();
    const { visible, spring } = useChromeVisibility();
    if (!visible)
        return null;
    return (_jsxs(animated.div, { style: {
            opacity: spring,
            transform: spring.to((progress) => `translate3d(0, ${(progress - 1) * 100}%, 0)`),
        }, children: [_jsx("div", { children: mobileHeader && isMobileStyle && _jsx(MobileProductHeader, {}) }), children] }));
};
export { Content, ContentHeader, ContentWrapper, Sidebar, Wrapper };
//# sourceMappingURL=AppLayoutV2.js.map